/* eslint-disable react/prop-types */
/* eslint-disable no-use-before-define */
/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import React from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $wrapNodeInElement, mergeRegister } from '@lexical/utils'
import {
  $createParagraphNode,
  $createRangeSelection,
  $getSelection,
  $insertNodes,
  $isNodeSelection,
  $isRootOrShadowRoot,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  createCommand,
  DRAGOVER_COMMAND,
  DRAGSTART_COMMAND,
  DROP_COMMAND
} from 'lexical'
import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { CAN_USE_DOM } from '../utils/canUseDOM'
import {
  $createInlineImageNode,
  $isInlineImageNode,
  InlineImageNode
} from '../nodes/InlineImageNode'
import Button from '../ui/Button'
import { DialogActions } from '../ui/Dialog'
import FileInput from '../ui/FileInput'
import TextInput from '../ui/TextInput'
import Select from '../ui/Select'
import '../ui/Checkbox.css'
import Label from '../../Label'


const getDOMSelection = targetWindow =>
  (CAN_USE_DOM ? (targetWindow || window).getSelection() : null)

export const INSERT_INLINE_IMAGE_COMMAND = createCommand(
  'INSERT_INLINE_IMAGE_COMMAND'
)

export function InsertInlineImageDialog({ activeEditor, onClose }) {
  const hasModifier = useRef(false)

  const [ src, setSrc ] = useState('')
  const [ altText, setAltText ] = useState('')
  const [ width, setWidth ] = useState('')
  const [ height, setHeight ] = useState('')
  const [ showCaption, setShowCaption ] = useState(false)
  const [ position, setPosition ] = useState('left')

  const isDisabled = src === ''

  const handleShowCaptionChange = e => {
    setShowCaption(e.target.checked)
  }

  const handlePositionChange = e => {
    setPosition(e.target.value)
  }

  const loadImage = files => {
    const reader = new FileReader()
    reader.onload = function() {
      if (typeof reader.result === 'string') {
        setSrc(reader.result)
      }
      return ''
    }
    if (files !== null) {
      reader.readAsDataURL(files[0])
    }
  }

  useEffect(() => {
    hasModifier.current = false
    const handler = e => {
      hasModifier.current = e.altKey
    }
    document.addEventListener('keydown', handler)
    return () => {
      document.removeEventListener('keydown', handler)
    }
  }, [ activeEditor ])

  const handleOnClick = () => {
    const payload = { altText, src, showCaption, position, width, height }
    activeEditor.dispatchCommand(INSERT_INLINE_IMAGE_COMMAND, payload)
    onClose()
  }
  return (
    <>
      <div className="input-group">
        <div className="form-group col-12">
          <FileInput
            label="Image Upload"
            onChange={loadImage}
            accept="image/*"
            data-test-id="image-modal-file-upload"
          />
        </div>
        <div className="form-group col-6">
          <TextInput
            label="Width"
            placeholder="Image width"
            onChange={setWidth}
            value={width}
            data-test-id="image-modal-alt-text-input"
          />
        </div>
        <div className="form-group col-6">
          <TextInput
            label="Height"
            placeholder="Image Height"
            onChange={setHeight}
            value={height}
            data-test-id="image-modal-alt-text-input"
          />
        </div>
        <div className="form-group col-12">
          <TextInput
            label="Alt Text"
            placeholder="Descriptive alternative text"
            onChange={setAltText}
            value={altText}
            data-test-id="image-modal-alt-text-input"
          />
        </div>

        <div className="form-group col-12">
          <Select
            label="Position"
            name="position"
            id="position-select"
            onChange={handlePositionChange}
            value={position}
            options={[
              { value: 'left', label: 'Left' },
              { value: 'right', label: 'Right' },
              { value: 'full', label: 'Full Width' }
            ]}
          />
        </div>

        <div className="form-group checkinput col-12">
          <div className="forminput">
            <label className="checkcontainer">
              <div>
                <input
                  className={classNames({ on: showCaption, off: !showCaption })}
                  id="caption"
                  type="checkbox"
                  checked={showCaption}
                  onChange={handleShowCaptionChange}
                />
                <span className="checkmark caption">
                  {showCaption ? <svg viewBox="0 0 24 24"><use href="/images/icons-16.svg#icon16-Check-Small" /></svg> : null}
                </span>
              </div>
            </label>
            <Label className="formlabel" htmlFor="caption">Show Caption</Label>
          </div>
        </div>
      </div>

      <DialogActions>
        <Button
          className="btn btn-primary"
          data-test-id="image-modal-file-upload-btn"
          disabled={isDisabled}
          onClick={() => handleOnClick()}
        >
          Confirm
        </Button>
      </DialogActions>
    </>
  )
}

export default function InlineImagePlugin({ captionsEnabled }) {
  const [ editor ] = useLexicalComposerContext()

  useEffect(() => {
    if (!editor.hasNodes([ InlineImageNode ])) {
      throw new Error('ImagesPlugin: ImageNode not registered on editor')
    }

    return mergeRegister(
      editor.registerCommand(
        INSERT_INLINE_IMAGE_COMMAND,
        payload => {
          const imageNode = $createInlineImageNode(payload)
          $insertNodes([ imageNode ])
          if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
            $wrapNodeInElement(imageNode, $createParagraphNode).selectEnd()
          }

          return true
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand(
        DRAGSTART_COMMAND,
        event => onDragStart(event),
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        DRAGOVER_COMMAND,
        event => onDragover(event),
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        DROP_COMMAND,
        event => onDrop(event, editor),
        COMMAND_PRIORITY_HIGH
      )
    )
  }, [ captionsEnabled, editor ])

  return null
}

const TRANSPARENT_IMAGE =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
const img = document.createElement('img')
img.src = TRANSPARENT_IMAGE

function onDragStart(event) {
  const node = getImageNodeInSelection()
  if (!node) {
    return false
  }
  const dataTransfer = event.dataTransfer
  if (!dataTransfer) {
    return false
  }
  dataTransfer.setData('text/plain', '_')
  dataTransfer.setDragImage(img, 0, 0)
  dataTransfer.setData(
    'application/x-lexical-drag',
    JSON.stringify({
      data: {
        altText: node.__altText,
        caption: node.__caption,
        height: node.__height,
        key: node.getKey(),
        showCaption: node.__showCaption,
        src: node.__src,
        width: node.__width
      },
      type: 'image'
    })
  )

  return true
}

function onDragover(event) {
  const node = getImageNodeInSelection()
  if (!node) {
    return false
  }
  if (!canDropImage(event)) {
    event.preventDefault()
  }
  return true
}

function onDrop(event, editor) {
  const node = getImageNodeInSelection()
  if (!node) {
    return false
  }
  const data = getDragImageData(event)
  if (!data) {
    return false
  }
  event.preventDefault()
  if (canDropImage(event)) {
    const range = getDragSelection(event)
    node.remove()
    const rangeSelection = $createRangeSelection()
    if (range !== null && range !== undefined) {
      rangeSelection.applyDOMRange(range)
    }
    $setSelection(rangeSelection)
    editor.dispatchCommand(INSERT_INLINE_IMAGE_COMMAND, data)
  }
  return true
}

function getImageNodeInSelection() {
  const selection = $getSelection()
  if (!$isNodeSelection(selection)) {
    return null
  }
  const nodes = selection.getNodes()
  const node = nodes[0]
  return $isInlineImageNode(node) ? node : null
}

function getDragImageData(event) {
  const dragData = event.dataTransfer?.getData('application/x-lexical-drag')
  if (!dragData) {
    return null
  }
  const { type, data } = JSON.parse(dragData)
  if (type !== 'image') {
    return null
  }

  return data
}

function canDropImage(event) {
  const target = event.target
  return !!(
    target &&
    target instanceof HTMLElement &&
    !target.closest('code, span.editor-image') &&
    target.parentElement &&
    target.parentElement.closest('div.editor-input')
  )
}

function getDragSelection(event) {
  let range
  const target = event.target
  const targetWindow =
    // eslint-disable-next-line no-nested-ternary
    target === null
      ? null
      : target.nodeType === 9
        ? target.defaultView
        : target.ownerDocument.defaultView
  const domSelection = getDOMSelection(targetWindow)
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(event.clientX, event.clientY)
  } else if (event.rangeParent && domSelection !== null) {
    domSelection.collapse(event.rangeParent, event.rangeOffset || 0)
    range = domSelection.getRangeAt(0)
  } else {
    throw Error('Cannot get the selection when dragging')
  }

  return range
}
